import { Typography } from 'antd'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { Tree } from '@/Ship/Components/Tree/Tree'

import { TaskTreeNode } from '../Tree/TaskTreeNode'
import { TaskTreeTabStore } from '../../Stores/TaskTreeTabStore'

import type { TaskFormContextLike } from '../../Contracts/Crud.types'
import type { TaskTreeNode as TaskTreeNodeContract } from '../../Contracts/Task.types'

const { Text } = Typography

interface Props {
    formContext?: TaskFormContextLike
}

/** Вузол дерева задач у формі, яку розуміє Ship-дерево (`id`/`parentId`). */
interface TaskTreeItem extends TaskTreeNodeContract {
    id: string
    parentId: string | null
}

/**
 * Вкладка «Дерево» картки задачі (SPEC-008 §5.4).
 *
 * Per-mount стор `TaskTreeTabStore` → `GET /services/tasks/{uuid}/tree` (N10):
 * плаский масив вузлів ([[GAP-NESTED-TREE]]/[[DEC-NESTED-TREE]] — adjacency list
 * за `parentUuid`) → Ship-дерево, яке рахує розкладку (entitree-flex) і малює
 * полотно. Поточна задача підсвічена, видалені — приглушені.
 * Клік по вузлу → картка задачі (`OpenResourceCardTask`, у `TaskTreeNode`).
 */
export const TaskTreeTab = observer(({ formContext }: Props) => {
    const [store] = useState(() => new TaskTreeTabStore())

    const uuid: string | undefined = formContext?.values?.uuid

    useEffect(() => {
        void store.load(uuid)
    }, [store, uuid])

    // Читаємо в рендері — для observer-трекінгу стора.
    const treeNodes = store.nodes

    const items = useMemo<TaskTreeItem[]>(
        () =>
            treeNodes.map((node) => ({
                ...node,
                id: node.uuid,
                parentId: node.parentUuid ?? null,
            })),
        [treeNodes]
    )

    const renderNode = useCallback(
        (item: TaskTreeItem) => <TaskTreeNode item={item} currentUuid={uuid} />,
        [uuid]
    )

    if (!store.loading && treeNodes.length === 0) return <Text type="secondary">Дерево порожнє</Text>

    return (
        <Tree
            items={items}
            renderNode={renderNode}
            isLoading={store.loading}
            nodeWidth={240}
            nodeHeight={96}
            height="520px"
        />
    )
})
